/**
 * Marketplace Analytics service — SuperAdmin and publisher view into the
 * live performance of marketplace baskets. Backed by
 * /analytics/marketplace/baskets[/<id>[/positions]].
 */
import apiClient from './apiClient';
import type {
  DateRangePreset,
  DetailSection,
  MarketplaceDetailResponse,
  MarketplaceListResponse,
  MarketplacePositionsResponse,
} from '../types/marketplaceAnalytics';

const API_TYPE = 'api' as const;

export interface DetailParams {
  start?: string;
  end?: string;
  preset?: DateRangePreset;
  section?: DetailSection;
  /** `false` disables the Phase 4B today-live overlay (fast path / debug). */
  live?: boolean;
}

/**
 * List-page window params — when both `start` and `end` are supplied, the
 * backend sums rollup rows over that window per basket so the list's
 * "Trades" and "Live P&L" columns line up with the detail page's window
 * aggregate. Passing neither preserves the legacy lifetime-KPI response.
 */
export interface ListParams {
  start?: string;
  end?: string;
  preset?: DateRangePreset;
}

export interface TradesParams {
  start: string;
  end: string;
  cursor?: string;
  page_size?: number;
}

class MarketplaceAnalyticsService {
  /** List baskets the caller can analyze. SuperAdmin = all; publisher = own.
   *  Optional window params scope the `Trades` / `Live P&L` columns to the
   *  operator-picked range (same rollup-Query math as the detail page). */
  async list(params: ListParams = {}): Promise<MarketplaceListResponse> {
    const qs = new URLSearchParams();
    if (params.start) qs.set('start', params.start);
    if (params.end) qs.set('end', params.end);
    if (params.preset) qs.set('preset', params.preset);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiClient.get<MarketplaceListResponse>(
      `/analytics/marketplace/baskets${suffix}`,
      undefined,
      API_TYPE,
    );
  }

  /** Full detail for one basket — listing meta, live aggregate, subscribers.
   *  Phase 5A: accepts optional date-range / section / live params that the
   *  backend uses to scope the aggregate window + skip the overlay. */
  async detail(
    basketId: string,
    params: DetailParams = {},
  ): Promise<MarketplaceDetailResponse> {
    const qs = new URLSearchParams();
    if (params.start) qs.set('start', params.start);
    if (params.end) qs.set('end', params.end);
    if (params.preset) qs.set('preset', params.preset);
    if (params.section) qs.set('section', params.section);
    if (params.live === false) qs.set('live', 'false');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiClient.get<MarketplaceDetailResponse>(
      `/analytics/marketplace/baskets/${encodeURIComponent(basketId)}${suffix}`,
      undefined,
      API_TYPE,
    );
  }

  /** Drill into one subscriber's positions for this basket. */
  async subscriberPositions(
    basketId: string,
    subscriberUserId: string,
  ): Promise<MarketplacePositionsResponse> {
    const params = new URLSearchParams({ subscriber_user_id: subscriberUserId });
    return apiClient.get<MarketplacePositionsResponse>(
      `/analytics/marketplace/baskets/${encodeURIComponent(basketId)}/positions?${params.toString()}`,
      undefined,
      API_TYPE,
    );
  }
}

const marketplaceAnalyticsService = new MarketplaceAnalyticsService();
export default marketplaceAnalyticsService;
